import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { Editor } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import Link from '@tiptap/extension-link';
import CharacterCount from '@tiptap/extension-character-count';
import { MdTypography } from '@antiqua-dls/vue-components';
import i18n from 'src/assets/i18n/i18n';
import { normalizeSpaces } from 'src/utils/helper/misc';
import { debounce } from 'lodash';
import { AI_RESPONSE_ERROR_TYPES, DESCRIPTION_CHAR_LIMITS, VALIDATION_EVENT_TYPE } from 'src/utils/constants/common.js';

import { adobeCjaEventEmitter } from '../../../../../../modules/adobe-cja/utils';
import { CJA_EVENTS, LINK_TYPES, LINK_CATEGORIES, COMMON, AI_ACTION_MAP } from '../../../../../../modules/adobe-cja/constants';
import EditorBar from '../components/EditorBar/index.vue';
import EditorBody from '../components/EditorBody/index.vue';
import EditorFooter from '../components/EditorFooter/index.vue';
import AiConsentModal from '../components/AiConsentModal/index.vue';
import ErrorContainer from '../components/ErrorsContainer/index.vue';
import { useAiTextEditor } from '../../../../composables/useAiTextEditor/index.js';

const { t } = i18n().global;

const defaultPlaceholder = {
  ai_disabled: t('pages.aiTextEditor.ai_editor_default_placeholder'),
  ai_enabled: t('pages.aiTextEditor.ai_editor_default_placeholder')
};

export default {
  name: 'AiTextEditor',
  components: {
    EditorBar,
    EditorBody,
    EditorFooter,
    ErrorContainer,
    AiConsentModal,
    MdTypography
  },
  props: {
    config: {
      type: Object,
      default: () => ({
        aiEnabled: false, // this acts as a feature flag to enable AI features
        placeholder: defaultPlaceholder,
        forcedErrors: [],
        updateConsent: true,
        aiActions: {
          boostVisibility: true,
          polishWriting: true,
          fixGrammar: true,
          shortenText: true
        },
        aiFooter: {
          tooltipDistance: 7
        },
        assetType: '',
        assetId: ''
      }),
      required: false
    },
    initialValue: {
      type: String,
      default: ''
    },
    validateOnMount: {
      type: Boolean,
      default: true
    },
    foundryId: {
      type: String,
      default: ''
    },
    validationTriggerKey: {
      type: String,
      default: ''
    }
  },
  emits: ['onChange', 'onChangeHtml', 'onFocus', 'onBlur', 'onAiConsent', 'onBannedWordUpdate', 'onBlurValidationDone'],
  setup(props, { emit }) {
    // composables
    const { aiSuggestionCallback, feedbackGiven, updateFeedback, hasConsentedToAi, aiAgreement, hasRoleCompute } =
      useAiTextEditor();

    // States
    const editor = ref(null);
    const diffEditor = ref(null);
    // Auto-enable AI if user has consented and feature is enabled
    const isAiEnabled = ref(false);
    const isDiffView = ref(false);
    const isAiResponseLoading = ref(false);
    const aiResponseError = ref({
      disableActions: false,
      errorMessage: null
    });
    const showAiConsentModal = ref(false);
    const aiSuggestedText = ref('');
    const aiSuggestedErrors = ref([]);
    const forcedErrors = ref([]);
    const errorStatus = ref(false);
    const lastAiAction = ref(null);
    const lastReplacedAction = ref(null);
    const isRequestCancelled = ref(false);
    const isContentDiffOnBlur = ref(false);
    const currentFeedbackId = ref('');
    const disableAIActionBasedOnValidation = ref(false);
    const userInteractedWithEditor = ref(false);
    const characterCount = ref({
      minLimit: props.config?.charLimits?.minCharLimit || DESCRIPTION_CHAR_LIMITS.minCharLimit,
      maxLimit: props.config?.charLimits?.maxCharLimit || DESCRIPTION_CHAR_LIMITS.maxCharLimit,
      editorMaxLimit: props.config?.charLimits?.editorMaxLimit || DESCRIPTION_CHAR_LIMITS.editorMaxLimit,
      editor: 0,
      aiSuggestion: 0
    });
    const validationData = ref({
      eventId: Date.now(),
      text: '',
      textLength: 0
    });

    // Computed properties
    const editorConfig = computed(() => {
      const isFsUser = hasRoleCompute.value(['foundrySupport']);
      forcedErrors.value = props.config.forcedErrors || [];
      const updatedConfig = {
        ...props.config
      };
      if (isFsUser) {
        updatedConfig.aiEnabled = false;
      }
      return updatedConfig;
    });

    const characterCountLimit = computed(() => {
      return (props.config?.charLimits?.displayCharLimit || DESCRIPTION_CHAR_LIMITS.displayCharLimit) + '+';
    });

    // Methods
    const getEditorContent = (editor) => {
      return editor?.isEmpty ? {
        html: '',
        text: ''
      } : {
        html: editor?.getHTML(),
        text: editor?.getText()
      }
    };

    /**
     * Handles content normalization and editor updates on blur
     */
    const normalizeEditorContent = (editor) => {
      // Early return if editor is invalid
      if (!editor?.getHTML) {
        return false;
      }

      try {
        const currentHtml = editor.getHTML();
        const normalizedHtml = normalizeSpaces(currentHtml);

        if (currentHtml === normalizedHtml) {
          return false; // No changes needed
        }

        // Update editor content with normalized spaces
        editor.commands.setContent(normalizedHtml, false);

        // Emit onChange event with normalized text
        const normalizedText = editor.getText();
        emit('onChange', normalizedText);
        emit('onChangeHtml', normalizedHtml);

        // Update character count
        characterCount.value.editor = editor.storage.characterCount.characters();

        return true; // Content was normalized
      } catch (error) {
        return false;
      }
    };

    const handlePlaceholder = editorInstance => {
      let placeholder = '';
      if (!editorInstance?.editor?.isFocused) {
        placeholder = isAiEnabled.value
          ? editorConfig.value.placeholder.ai_enabled
          : editorConfig.value.placeholder.ai_disabled;
      }
      return placeholder;
    };

    const emitAnalyticsEvent = (linkName, linkType = LINK_TYPES.BUTTON_CLICK) => {
      const analyticsPayload = {
        event: CJA_EVENTS.LINK_CLICK,
        links: {
          linkCategory: LINK_CATEGORIES.AI_ASSISTANT,
          linkSection: `${props.config.assetType}_description`,
          linkName: linkName,
          linkType: linkType
        }
      };
      adobeCjaEventEmitter(analyticsPayload);
    };

    const updateTextForValidation = (eventType, { html, text }) => {
      validationData.value = {
        eventId: Date.now(),
        eventType: eventType,
        text: html,
        plainText: text,
        textLength: characterCount.value.editor,
        minLimit: characterCount.value.minLimit,
        maxLimit: characterCount.value.maxLimit
      };
    };

    const resetAiStates = () => {
      isRequestCancelled.value = true;
      isDiffView.value = false;

      if (aiSuggestedText.value) {
        aiSuggestedText.value = '';
      }

      if (aiSuggestedErrors.value.length) {
        aiSuggestedErrors.value = [];
      }

      isAiResponseLoading.value = false;

      if (diffEditor.value) {
        diffEditor.value.destroy();
        diffEditor.value = null;
      }

      characterCount.value.aiSuggestion = 0;

      const updateFeedbackParams = {
        feedbackId: currentFeedbackId.value,
        feedback: false
      };
      updateFeedback(updateFeedbackParams);
    };

    // Helper function to update editor validation
    const updateEditorValidation = () => {
      const hasCharacters = editor.value.storage.characterCount.characters();
      const hasUserInteraction = userInteractedWithEditor.value;

      if (hasCharacters && hasUserInteraction) {
        characterCount.value.editor = editor.value.storage.characterCount.characters();
        updateTextForValidation(VALIDATION_EVENT_TYPE.CHANGE, getEditorContent(editor.value));
      }
    };

    // Helper function to reset error state
    const resetErrorState = () => {
      if (!aiResponseError.value.disableActions) {
        aiResponseError.value = {
          disableActions: false,
          errorMessage: null
        };
      }
    };

    const handleAiToggle = value => {
      // Always emit analytics event first, regardless of conditions
      emitAnalyticsEvent(`aiAssistantStatus_${value ? 'on' : 'off'}`, LINK_TYPES.TOGGLE_INTERACTION);

      if (!editorConfig.value.aiEnabled) {
        return;
      }

      if (value && !hasConsentedToAi.value) {
        showAiConsentModal.value = true;
        return;
      }

      isAiEnabled.value = value;

      if (!value) {
        isAiEnabled.value = false;
        resetAiStates();
      }

      updateEditorValidation();
      resetErrorState();
    };

    const editorOnFocus = ({ editor: focusedEditor }) => {
      emit('onFocus', Date.now());
    };

    /**
     * Handles editor blur event with content normalization and validation
     */
    const editorOnBlur = ({ editor: blurredEditor }) => {
      // Update interaction state
      userInteractedWithEditor.value = true;

      // Reset AI suggestion errors if content has been modified
      if (isContentDiffOnBlur.value) {
        aiSuggestedErrors.value = [];
        isContentDiffOnBlur.value = false;
      }

      // Normalize spaces in content - this is a side effect that improves UX
      normalizeEditorContent(blurredEditor);

      // Emit blur event
      emit('onBlur', Date.now());

      // Trigger validation
      updateTextForValidation(VALIDATION_EVENT_TYPE.BLUR, getEditorContent(blurredEditor));
    };

    const onChangeValidate = updatedEditor => {
      updateTextForValidation(VALIDATION_EVENT_TYPE.CHANGE, getEditorContent(updatedEditor));
    };

    const onChangeValidateDebounced = debounce(onChangeValidate, 50);

    const editorOnChange = ({ editor: updatedEditor }) => {
      userInteractedWithEditor.value = true;
      const { html, text } = getEditorContent(updatedEditor);
      emit('onChange', text);
      emit('onChangeHtml', html);
      characterCount.value.editor = updatedEditor.storage.characterCount.characters();
      isContentDiffOnBlur.value = true;
      onChangeValidateDebounced(updatedEditor);
    };

    const handleErrorStatus = ({ hasError, disableAIAction }) => {
      errorStatus.value = hasError;
      disableAIActionBasedOnValidation.value = disableAIAction;
    };

    const onValidationDone = status => {
      emit('onBlurValidationDone', status);
    };

    const handleBannedWordResult = event => {
      emit('onBannedWordUpdate', event.status);
      if (!event.text || !editor.value) return;

      const currentContent = editor.value.getHTML();

      // Only update content if it's actually different
      if (event.text !== currentContent) {
        // Clear any existing strike formatting before setting new content
        editor.value.commands.unsetStrike();

        // Set new content
        editor.value.commands.setContent(event.text, false);

        // Ensure strike formatting is cleared after content is set
        setTimeout(() => {
          if (editor.value) {
            editor.value.commands.unsetStrike();
            // Additional check to clear any residual strike formatting
            if (editor.value.isActive('strike')) {
              editor.value.commands.unsetStrike();
            }
          }
        }, 0);
      }
    };

    const handleAiAction = async action => {
      if (!editorConfig.value.aiEnabled || !isAiEnabled.value || isAiResponseLoading.value) {
        console.debug('AI features are not enabled or already in progress.');
        return;
      }

      userInteractedWithEditor.value = true;
      isAiResponseLoading.value = true;
      lastAiAction.value = action; // Track which action is being performed
      aiResponseError.value = {
        disableActions: false,
        errorMessage: null
      };

      isRequestCancelled.value = false;
      const originalText = editor.value.getHTML();
      const diffEditorText = diffEditor.value ? diffEditor.value.getHTML() : '';
      const diffEditorWasOpen = isDiffView.value && diffEditorText !== originalText;
      const textToUse = diffEditorWasOpen ? diffEditorText : originalText;
      const suggestionPayload = {
        action,
        description: textToUse,
        assetType: props.config.assetType,
        assetId: props.config.assetId
      };

      // make the AI suggestion call
      const response = await aiSuggestionCallback(suggestionPayload);

      if (isRequestCancelled.value || !response?.success) {
        isAiResponseLoading.value = false;
        const _aiResponseError = {
          disableActions: false,
          errorMessage: response.error.message
        };
        if (response.error.code === AI_RESPONSE_ERROR_TYPES.rateLimit) {
          _aiResponseError.disableActions = true;
          emitAnalyticsEvent('usageLimitReached_true_limitType_monthly', LINK_TYPES.SYSTEM);
        }
        aiResponseError.value = _aiResponseError;
        return;
      }

      // this snippet handles the ai error if came in response
      if (response?.success && response.data?.aiErrors?.length > 0) {
        aiSuggestedErrors.value = response.data?.aiErrors;
        isAiResponseLoading.value = false;
        emitAnalyticsEvent('usageLimitReached_false_limitType_monthly', LINK_TYPES.SYSTEM);
        return;
      }

      aiSuggestedText.value = response.data.description || '';
      currentFeedbackId.value = response?.data?.aiFeedbackId;
      emitAnalyticsEvent('usageLimitReached_false_limitType_monthly', LINK_TYPES.SYSTEM);
      // Transform action name for AI analytics using the mapping
      const transformedAction = AI_ACTION_MAP[action] || action;
      emitAnalyticsEvent(`aiActionUsed_${transformedAction}`);

      if (diffEditor.value) {
        diffEditor.value.destroy();
      }

      diffEditor.value = new Editor({
        editable: true,
        extensions: [
          StarterKit,
          Strike,
          Link.configure({
            openOnClick: false
          }),
          Placeholder.configure({
            placeholder: handlePlaceholder
          }),
          CharacterCount.configure({ limit: characterCount.value.editorMaxLimit })
        ],
        content: textToUse,
        onCreate: () => {
          console.debug('Editor created!');
        },
        onUpdate: editorOnChange,
        onBlur: editorOnBlur,
        onFocus: editorOnFocus,
        onSelectionUpdate: ({ editor: currentEditor }) => {
          // Ensure new content doesn't inherit strike formatting
          if (currentEditor.isActive('strike')) {
            setTimeout(() => {
              currentEditor.commands.unsetStrike();
            }, 0);
          }
        }
      });
      characterCount.value.editor = diffEditor.value.storage.characterCount.characters();

      // Create a temporary editor to get accurate character count for AI suggestion
      const tempEditor = new Editor({
        extensions: [StarterKit, CharacterCount.configure({ limit: characterCount.value.editorMaxLimit })],
        content: aiSuggestedText.value
      });
      characterCount.value.aiSuggestion = tempEditor.storage.characterCount.characters();
      tempEditor.destroy();
      isDiffView.value = true;
      isAiResponseLoading.value = false;
    };

    const enableAiFeatures = featureAgreementId => {
      if (!editorConfig.value.aiEnabled) {
        return;
      }
      emit('onAiConsent', featureAgreementId);
      hasConsentedToAi.value = true;
      // Auto-enable AI immediately after consent
      isAiEnabled.value = true;
      showAiConsentModal.value = false;
    };

    const handleConsentCancel = () => {
      showAiConsentModal.value = false;
      // Ensure the toggle stays off when user cancels
      isAiEnabled.value = false;
    };

    const discardSuggestion = () => {
      userInteractedWithEditor.value = true;
      isDiffView.value = false;
      aiSuggestedText.value = '';
      lastAiAction.value = null; // Clear the last action when discarding
      if (diffEditor.value) {
        diffEditor.value.destroy();
        diffEditor.value = null;
      }
      characterCount.value.editor = editor.value.storage.characterCount.characters();
      updateTextForValidation(VALIDATION_EVENT_TYPE.CHANGE, getEditorContent(editor.value));
      const updateFeedbackParams = {
        feedbackId: currentFeedbackId.value,
        feedback: false
      };
      emitAnalyticsEvent(COMMON.SUGGESTION_DISCARDED);
      updateFeedback(updateFeedbackParams);
    };

    const replaceSuggestion = () => {
      userInteractedWithEditor.value = true;
      editor.value.commands.setContent(aiSuggestedText.value);
      const plainText = editor.value.getText();
      const htmlText = editor.value.getHTML();
      emit('onChange', plainText);
      emit('onChangeHtml', htmlText);
      characterCount.value.editor = editor.value.storage.characterCount.characters();

      // Set the replaced action to trigger success state
      lastReplacedAction.value = lastAiAction.value;

      isDiffView.value = false;
      if (diffEditor.value) {
        diffEditor.value.destroy();
        diffEditor.value = null;
      }
      updateTextForValidation(VALIDATION_EVENT_TYPE.BLUR, { html: htmlText, text: plainText });

      setTimeout(() => {
        lastReplacedAction.value = null;
      }, 2100);

      const updateFeedbackParams = {
        feedbackId: currentFeedbackId.value,
        feedback: false,
        isAccept: true
      };
      emitAnalyticsEvent(COMMON.SUGGESTION_ACCEPTED);
      updateFeedback(updateFeedbackParams);
    };

    // Lifecycle hooks
    onMounted(() => {
      aiAgreement.value = '';
      editor.value = new Editor({
        extensions: [
          StarterKit,
          Strike,
          Link.configure({
            openOnClick: false
          }),
          Placeholder.configure({
            placeholder: handlePlaceholder
          }),
          CharacterCount.configure({ limit: characterCount.value.editorMaxLimit })
        ],
        content: props.initialValue || '',
        onCreate: () => {
          console.debug('Editor created!');
        },
        onUpdate: editorOnChange,
        onBlur: editorOnBlur,
        onFocus: editorOnFocus,
        onSelectionUpdate: ({ editor: currentEditor }) => {
          // Ensure new content doesn't inherit strike formatting
          if (currentEditor.isActive('strike')) {
            setTimeout(() => {
              currentEditor.commands.unsetStrike();
            }, 0);
          }
        }
      });
      characterCount.value.editor = editor.value.storage.characterCount.characters();

      if (props.initialValue && props.validateOnMount) {
        updateTextForValidation(VALIDATION_EVENT_TYPE.BLUR, getEditorContent(editor.value));
      }

      //Kept condition here because single use onMount only
      if(editorConfig.value.aiEnabled && hasConsentedToAi.value) {
        isAiEnabled.value = true;
        updateEditorValidation();
        resetErrorState();
      }
    });

    onBeforeUnmount(() => {
      editor.value.destroy();
      if (diffEditor.value) {
        diffEditor.value.destroy();
      }
    });

    watch(isAiEnabled, () => {
      editor.value.chain().run();
    });

    watch(
      () => props.validationTriggerKey,
      () => {
        if (props.validationTriggerKey && (props.validateOnMount || userInteractedWithEditor.value)) {
          updateTextForValidation(VALIDATION_EVENT_TYPE.BLUR, getEditorContent(editor.value));
        }
      }
    );

    return {
      editor,
      editorConfig,
      emitAnalyticsEvent,
      isAiEnabled,
      isRequestCancelled,
      showAiConsentModal,
      isDiffView,
      aiSuggestedText,
      forcedErrors,
      characterCount,
      diffEditor,
      isAiResponseLoading,
      handleAiToggle,
      resetAiStates,
      resetErrorState,
      updateEditorValidation,
      updateTextForValidation,
      enableAiFeatures,
      handleConsentCancel,
      discardSuggestion,
      replaceSuggestion,
      handleAiAction,
      aiSuggestedErrors,
      validationData,
      handleErrorStatus,
      onValidationDone,
      lastAiAction,
      lastReplacedAction,
      feedbackGiven,
      updateFeedback,
      hasConsentedToAi,
      editorOnChange,
      editorOnBlur,
      editorOnFocus,
      handleBannedWordResult,
      handlePlaceholder,
      errorStatus,
      aiResponseError,
      characterCountLimit,
      currentFeedbackId,
      disableAIActionBasedOnValidation,
      userInteractedWithEditor,
      normalizeEditorContent
    };
  }
};
